import type { ReactElement } from 'react';
import type { AutomationScheduleFrequency, AutomationWeekday } from '@molt/shared';
import { TextField } from '@/components';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface AutomationScheduleDraft {
  frequency: AutomationScheduleFrequency;
  daysOfWeek: AutomationWeekday[];
  dayOfMonth: number | null;
  monthDay: string | null;
  time: string;
  timezone: string;
}

export interface AutomationScheduleFieldsProps {
  schedule: AutomationScheduleDraft;
  dueDateOffsetMinutes: number;
  mode: 'calendar_command' | 'due_date_command';
  disabled?: boolean | undefined;
  onScheduleChange: (schedule: AutomationScheduleDraft) => void;
  onDueDateOffsetMinutesChange: (minutes: number) => void;
}

/**
 * ⚠ THE CONSTANT HOLDS THE KEY AND THE RENDER RESOLVES IT. A module-level
 * constant has no context to call `t()` from, so the seven weekday words are
 * declared as catalogue keys here and read as `t(WEEKDAY_KEYS[day])` below —
 * the `BOARD_ROLE_LABELS` shape. No `t('…')` anchor can see these seven; the
 * routing pin holds them by their string constants instead.
 *
 * ⚠ NOT shared with `planner.weekday.*`. Those are lower-case words inside a
 * planner rule sentence; these are `Monday`-cased checkbox labels on a board
 * panel. Different case, different surface, different node.
 */
const WEEKDAY_KEYS: Record<AutomationWeekday, string> = {
  monday: 'automationAuthoring.schedule.weekday.monday',
  tuesday: 'automationAuthoring.schedule.weekday.tuesday',
  wednesday: 'automationAuthoring.schedule.weekday.wednesday',
  thursday: 'automationAuthoring.schedule.weekday.thursday',
  friday: 'automationAuthoring.schedule.weekday.friday',
  saturday: 'automationAuthoring.schedule.weekday.saturday',
  sunday: 'automationAuthoring.schedule.weekday.sunday',
};

const WEEKDAY_ORDER: readonly AutomationWeekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

function offsetParts(minutes: number): {
  relation: 'before' | 'at' | 'after';
  amount: number;
  unit: 'minutes' | 'hours' | 'days';
} {
  if (minutes === 0) return { relation: 'at', amount: 0, unit: 'minutes' };
  const abs = Math.abs(minutes);
  if (abs % 1440 === 0) {
    return {
      relation: minutes > 0 ? 'before' : 'after',
      amount: abs / 1440,
      unit: 'days',
    };
  }
  if (abs % 60 === 0) {
    return {
      relation: minutes > 0 ? 'before' : 'after',
      amount: abs / 60,
      unit: 'hours',
    };
  }
  return { relation: minutes > 0 ? 'before' : 'after', amount: abs, unit: 'minutes' };
}

function minutesFromParts(
  relation: 'before' | 'at' | 'after',
  amount: number,
  unit: 'minutes' | 'hours' | 'days',
): number {
  if (relation === 'at') return 0;
  const multiplier = unit === 'days' ? 1440 : unit === 'hours' ? 60 : 1;
  const value = Math.max(0, Math.trunc(amount)) * multiplier;
  return relation === 'before' ? value : -value;
}

function scheduleForFrequency(
  schedule: AutomationScheduleDraft,
  frequency: AutomationScheduleFrequency,
): AutomationScheduleDraft {
  return {
    ...schedule,
    frequency,
    dayOfMonth:
      frequency === 'monthly' && schedule.dayOfMonth === null ? 1 : schedule.dayOfMonth,
    monthDay:
      frequency === 'yearly' && schedule.monthDay === null ? '01-01' : schedule.monthDay,
  };
}

export function AutomationScheduleFields({
  schedule,
  dueDateOffsetMinutes,
  mode,
  disabled = false,
  onScheduleChange,
  onDueDateOffsetMinutesChange,
}: AutomationScheduleFieldsProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const offset = offsetParts(dueDateOffsetMinutes);

  function patchSchedule(patch: Partial<AutomationScheduleDraft>): void {
    onScheduleChange({ ...schedule, ...patch });
  }

  if (mode === 'due_date_command') {
    return (
      <fieldset className="flex flex-col gap-3 rounded-card border border-border-strong bg-surface-raised p-3">
        <legend className="px-1 text-sm font-semibold text-ink">
          {t('automationAuthoring.dueTrigger.legend')}
        </legend>
        <label className="flex flex-col gap-1 text-sm font-medium text-ink">
          {t('automationAuthoring.dueTrigger.relationLabel')}
          <select
            className="h-9 rounded-card border border-border-strong bg-surface px-3 text-card text-ink focus:border-accent"
            value={offset.relation}
            disabled={disabled}
            onChange={(event) => {
              onDueDateOffsetMinutesChange(
                minutesFromParts(
                  event.currentTarget.value as 'before' | 'at' | 'after',
                  offset.amount,
                  offset.unit,
                ),
              );
            }}
          >
            <option value="before">
              {t('automationAuthoring.dueTrigger.relation.before')}
            </option>
            <option value="at">{t('automationAuthoring.dueTrigger.relation.at')}</option>
            <option value="after">
              {t('automationAuthoring.dueTrigger.relation.after')}
            </option>
          </select>
        </label>
        {offset.relation !== 'at' ? (
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_8rem]">
            <TextField
              label={t('automationAuthoring.dueTrigger.amountLabel')}
              type="number"
              min={1}
              value={offset.amount}
              disabled={disabled}
              onChange={(event) => {
                onDueDateOffsetMinutesChange(
                  minutesFromParts(
                    offset.relation,
                    Number(event.currentTarget.value),
                    offset.unit,
                  ),
                );
              }}
            />
            <label className="flex flex-col gap-1 text-sm font-medium text-ink">
              {t('automationAuthoring.dueTrigger.unitLabel')}
              <select
                className="h-9 rounded-card border border-border-strong bg-surface px-3 text-card text-ink focus:border-accent"
                value={offset.unit}
                disabled={disabled}
                onChange={(event) => {
                  onDueDateOffsetMinutesChange(
                    minutesFromParts(
                      offset.relation,
                      offset.amount,
                      event.currentTarget.value as 'minutes' | 'hours' | 'days',
                    ),
                  );
                }}
              >
                <option value="minutes">
                  {t('automationAuthoring.dueTrigger.unit.minutes')}
                </option>
                <option value="hours">
                  {t('automationAuthoring.dueTrigger.unit.hours')}
                </option>
                <option value="days">
                  {t('automationAuthoring.dueTrigger.unit.days')}
                </option>
              </select>
            </label>
          </div>
        ) : null}
      </fieldset>
    );
  }

  return (
    <fieldset className="flex flex-col gap-3 rounded-card border border-border-strong bg-surface-raised p-3">
      <legend className="px-1 text-sm font-semibold text-ink">
        {t('automationAuthoring.schedule.legend')}
      </legend>
      <label className="flex flex-col gap-1 text-sm font-medium text-ink">
        {t('automationAuthoring.schedule.frequencyLabel')}
        <select
          className="h-9 rounded-card border border-border-strong bg-surface px-3 text-card text-ink focus:border-accent"
          value={schedule.frequency}
          disabled={disabled}
          onChange={(event) =>
            onScheduleChange(
              scheduleForFrequency(
                schedule,
                event.currentTarget.value as AutomationScheduleFrequency,
              ),
            )
          }
        >
          <option value="daily">
            {t('automationAuthoring.schedule.frequency.daily')}
          </option>
          <option value="weekdays">
            {t('automationAuthoring.schedule.frequency.weekdays')}
          </option>
          <option value="weekly">
            {t('automationAuthoring.schedule.frequency.weekly')}
          </option>
          <option value="monthly">
            {t('automationAuthoring.schedule.frequency.monthly')}
          </option>
          <option value="yearly">
            {t('automationAuthoring.schedule.frequency.yearly')}
          </option>
        </select>
      </label>
      {schedule.frequency === 'weekly' ? (
        <div
          className="flex flex-wrap gap-2"
          aria-label={t('automationAuthoring.schedule.weekdaysLabel')}
        >
          {WEEKDAY_ORDER.map((day) => {
            const selected = schedule.daysOfWeek.includes(day);
            return (
              <label key={day} className="flex items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={selected}
                  disabled={disabled}
                  onChange={() => {
                    patchSchedule({
                      daysOfWeek: selected
                        ? schedule.daysOfWeek.filter((value) => value !== day)
                        : [...schedule.daysOfWeek, day],
                    });
                  }}
                />
                {t(WEEKDAY_KEYS[day])}
              </label>
            );
          })}
        </div>
      ) : null}
      {schedule.frequency === 'monthly' ? (
        <TextField
          label={t('automationAuthoring.schedule.dayOfMonthLabel')}
          type="number"
          min={1}
          max={31}
          value={schedule.dayOfMonth ?? 1}
          disabled={disabled}
          onChange={(event) =>
            patchSchedule({ dayOfMonth: Number(event.currentTarget.value) })
          }
        />
      ) : null}
      {schedule.frequency === 'yearly' ? (
        <TextField
          label={t('automationAuthoring.schedule.yearlyDateLabel')}
          placeholder={t('automationAuthoring.schedule.yearlyDatePlaceholder')}
          value={schedule.monthDay ?? '01-01'}
          disabled={disabled}
          onChange={(event) => patchSchedule({ monthDay: event.currentTarget.value })}
        />
      ) : null}
      <div className="grid gap-2 sm:grid-cols-2">
        <TextField
          label={t('automationAuthoring.schedule.timeLabel')}
          type="time"
          value={schedule.time}
          disabled={disabled}
          onChange={(event) => patchSchedule({ time: event.currentTarget.value })}
        />
        <TextField
          label={t('automationAuthoring.schedule.timezoneLabel')}
          value={schedule.timezone}
          disabled={disabled}
          onChange={(event) => patchSchedule({ timezone: event.currentTarget.value })}
        />
      </div>
    </fieldset>
  );
}
